import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

/**
 * Composant d'image optimisé pour le logo
 * Utilise WebP avec fallback PNG et srcset pour les écrans retina
 * 
 * @example
 * <OptimizedLogo 
 *   src="/icon128.png" 
 *   alt="Logo"
 *   width={103}
 *   height={105}
 * />
 */
const OptimizedLogo = ({ 
  src = '/icon128.png',
  alt = 'Logo', 
  className = '',
  width,
  height,
  loading = 'eager', // Le logo est généralement above-the-fold
  style = {}
}) => {
  const [imageError, setImageError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const handleError = () => {
    setImageError(true);
  };

  useEffect(() => {
    setImageError(false);
    setIsLoaded(false);
  }, [src]);

  // Extraire le chemin de base sans extension
  const basePath = src.replace(/\.[^/.]+$/, '');

  // Si erreur, utiliser l'image originale
  if (imageError) {
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        style={{ ...style, width, height }}
        loading={loading}
        decoding="async"
      />
    );
  }

  return (
    <picture>
      {/* Source WebP avec srcset pour retina */}
      <source
        type="image/webp"
        srcSet={`${basePath}-sm.webp 1x, ${basePath}-md.webp 2x`}
      />
      
      {/* Source PNG avec srcset (fallback) */}
      <source
        type="image/png"
        srcSet={`${basePath}-sm.png 1x, ${basePath}-md.png 2x`}
      />
      
      {/* Image par défaut */}
      <img
        src={`${basePath}-sm.png`}
        alt={alt}
        className={className}
        style={{
          ...style,
          width,
          height
        }}
        width={width}
        height={height}
        loading={loading}
        decoding="async"
        onLoad={handleLoad}
        onError={handleError}
      />
    </picture>
  );
};

OptimizedLogo.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  className: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  loading: PropTypes.oneOf(['lazy', 'eager']),
  style: PropTypes.object
};

export default OptimizedLogo;
